import React from 'react';
import { DatePicker, Field, Grid, Select } from '@exxonmobil/react-unity';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { DomainValidation } from '../../common/types/DomainTypes';
import { DATE_FORMAT, INSTANT_FIRST_DAY_OF_THE_YEAR, INSTANT_FORMAT, INSTANT_TODAY } from '../../common/date';

interface Props {
  domainId: string;
  setTimePeriod: (value: Period) => void;
  setStartDateTime: (value: string) => void;
  startDateTime: string;
  setEndDateTime: (value: string) => void;
  endDateTime: string;
  setDomainId: (value: string) => void;
  setDomainName: (value: string) => void;
  domains: DomainValidation[];
  domainsIsLoading: boolean;
}

export enum Period {
  YTD = 'Year to date',
  Last30Days = 'Last 30 days',
  Last90Days = 'Last 90 days',
  Last180Days = 'Last 180 days',
  Last12Months = 'Last 12 months'
}

const convertToInstant = (value: string) => {
  return `${dayjs(value, 'ddd MMM DD YYYY').format(INSTANT_FORMAT)}Z`;
};

const convertToDisplayDate = (date: string) => {
  return dayjs(date, INSTANT_FORMAT).format(DATE_FORMAT);
};

const getInstantByPeriod = (period: string): [string, string] => {
  const getInstantDateFromXDaysAgo = (days: number) => {
    return `${dayjs().subtract(days, 'day').startOf('day').format(INSTANT_FORMAT)}Z`;
  };

  switch (period) {
    case Period.YTD:
      return [INSTANT_FIRST_DAY_OF_THE_YEAR, INSTANT_TODAY];
    case Period.Last30Days:
      return [getInstantDateFromXDaysAgo(30), INSTANT_TODAY];
    case Period.Last90Days:
      return [getInstantDateFromXDaysAgo(90), INSTANT_TODAY];
    case Period.Last180Days:
      return [getInstantDateFromXDaysAgo(180), INSTANT_TODAY];
    case Period.Last12Months:
      return [getInstantDateFromXDaysAgo(365), INSTANT_TODAY];
    default:
      throw new Error(`Non-existent period in switch: ${period}`);
  }
};

const TrendsFieldSet = ({
  setTimePeriod,
  setStartDateTime,
  startDateTime,
  setEndDateTime,
  endDateTime,
  setDomainId,
  domainId,
  setDomainName,
  domains,
  domainsIsLoading
}: Props) => {
  return (
    <Grid variant="4-up">
      <Grid.Item>
        <Field>
          <Field.Label> Date </Field.Label>
          <Field.Body>
            <StyledSelect
              onChange={(e) => {
                const [startTime, endTime] = getInstantByPeriod(e.target.value);
                setTimePeriod(e.target.value);
                setStartDateTime(startTime);
                setEndDateTime(endTime);
              }}
            >
              {Object.keys(Period).map((key) => (
                <option value={Period[key as keyof typeof Period]} key={Period[key as keyof typeof Period]}>
                  {Period[key as keyof typeof Period]}
                </option>
              ))}
            </StyledSelect>
          </Field.Body>
        </Field>
      </Grid.Item>
      <Grid.Item>
        <Field>
          <Field.Label> Domain </Field.Label>
          <Field.Body>
            <StyledSelect
              value={domainId}
              onChange={(e) => {
                setDomainId(e.target.value);
                setDomainName(e.target.options[e.target.selectedIndex].text);
              }}
            >
              <option value="all" key="all">
                All Domain
              </option>
              {domainsIsLoading ? (
                <option>Loading...</option>
              ) : (
                domains.map((domain) => (
                  <option value={domain.id} key={domain.id}>
                    {domain.name}
                  </option>
                ))
              )}
            </StyledSelect>
          </Field.Body>
        </Field>
      </Grid.Item>
      <Grid.Item>
        <DatePicker
          label="Start Time"
          value={convertToDisplayDate(startDateTime)}
          onChange={(e) => {
            setStartDateTime(convertToInstant(e.target.value));
          }}
          disabled
        />
      </Grid.Item>
      <Grid.Item>
        <DatePicker
          label="End Time"
          value={convertToDisplayDate(endDateTime)}
          onChange={(e) => {
            setEndDateTime(convertToInstant(e.target.value));
          }}
          disabled
        />
      </Grid.Item>
    </Grid>
  );
};

const StyledSelect = styled(Select)`
  background: white;
  & .em-c-select {
    width: 100%;
  }
`;

export default TrendsFieldSet;
